import React, { FC, JSXElementConstructor, ReactNode } from 'react';

import GroupedByBundleItem from '../bundles/GroupedByBundleItem';
import * as types from '../../store/constants';
import { ACTION_DECREASE, ACTION_INCREASE, ACTION_SET } from '../../store/constants';
import { getGroupItemId, GroupingState, GroupItem } from '../../../models/GroupItem';
import { SelectionList } from '../../../models/SelectionList';
import classNames from 'classnames';
import { ListProduct, ProductMetaData } from '../../../models/Product';
import {
    BulkActionResponse,
    BulkAddProductsToSelectionListResponse,
    BulkAddProductsToShoppingCartResponse,
    LineItemKey
} from '../../store/api';
import { Article } from '../../../models/Article';
import { useGroupBySearchParam } from './useGroupBySearchParam';
import { SingleAddOptions, SingleCopyOrMoveOptions } from '../../../models/ListContentCheckOptions';
import { ProductSelection } from '../product-selection/ProductSelectionTypes';
import { DisplayType } from './displayType/types';
import { AdditionalItemRenderProps, ProductListItemProps } from './ProductList';

export type GroupedByItemProps = {
    readonly allowSelection?: boolean;
    readonly children: (props: ProductListItemProps & AdditionalItemRenderProps) => ReactNode;
    readonly currentListId?: string;
    readonly displayType: DisplayType;
    readonly grouping: GroupingState;
    readonly isGroupAddAction?: boolean;
    readonly isProductSelectable: boolean;
    readonly item: GroupItem;
    readonly onAddPrice: (options: SingleAddOptions | SingleCopyOrMoveOptions) => Promise<void>;
    readonly onCopyGroupToList: (
        item: GroupItem,
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onCopyGroupedItemToCart?: (
        bundleId: string,
        performChecks: boolean
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onGroupAmountChange?: (
        item: GroupItem,
        amount: number,
        command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
    ) => Promise<BulkActionResponse>;
    readonly onGroupBy?: (groupBy: { readonly key: string; readonly label?: string }) => void;
    readonly onMoveGroupToList: (
        item: GroupItem,
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onRemoveGroupedItemFromList?: () => Promise<void>;
    readonly onSelectProduct: (lineItemKey: LineItemKey) => void;
    readonly onToggleList: () => void;
    readonly pageSize: number;
    readonly productSelection: ProductSelection;
    readonly products: ListProduct[];
    readonly showSelectedPriceOnly?: boolean;
    readonly totalProducts: number | undefined;
};

type Props = {
    readonly groups: ReadonlyArray<GroupItem>;
    readonly grouping: GroupingState;
    readonly isGroupAddAction?: boolean;
    readonly cancelProductSelection: () => void;
    readonly products: ListProduct[];
    readonly onSelectProduct: (lineItemKey: LineItemKey) => void;
    readonly onCopyGroupedItemToCart?: (
        bundleId: string,
        performChecks: boolean
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onRemoveGroupedItemFromList?: (item: GroupItem) => Promise<void>;
    readonly onGroupAmountChange?: (
        item: GroupItem,
        amount: number,
        command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
    ) => Promise<BulkActionResponse>;
    readonly onCopyGroupToList: (
        item: GroupItem,
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly onMoveGroupToList: (
        item: GroupItem,
        list: SelectionList,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
    readonly invalidateAggregations: () => Promise<void>;
    readonly itemComponent: JSXElementConstructor<GroupedByItemProps>;
    readonly totalProducts: number | undefined;
    readonly currentPage: number;
    readonly pageSize: number;
    readonly onAddPrice: (options: SingleAddOptions | SingleCopyOrMoveOptions) => Promise<void>;
    readonly displayType: DisplayType;
    readonly onRemoveProductFromList?: (product: ProductMetaData & Article) => Promise<void>;
    readonly productSelection?: ProductSelection;
    readonly isProductSelectable: boolean;
    readonly children: (props: ProductListItemProps & AdditionalItemRenderProps) => ReactNode;
};

const GroupedByList: FC<Props> = props => {
    const { replaceURLGroupByEntry, deleteURLGroupByEntry } = useGroupBySearchParam();

    function toggleList(groupItemId: string) {
        const id = groupItemId === props.grouping.id ? null : groupItemId;

        const newGrouping = { ...props.grouping, ...{ id } };

        if (id || (!id && newGrouping.key !== types.GROUPBY_PRODUCTS)) {
            replaceURLGroupByEntry(newGrouping);
            props.cancelProductSelection();
            props.invalidateAggregations().then();
        } else {
            deleteURLGroupByEntry();
        }
    }

    const ItemComponent = props.itemComponent;

    return (
        <div className={classNames('groupby', props.grouping.key)}>
            {props.groups.map(item => {
                const itemId = getGroupItemId(item);

                return (
                    <ItemComponent
                        {...props}
                        productSelection={props.productSelection}
                        key={itemId}
                        item={item}
                        onToggleList={() => toggleList(itemId)}
                        onRemoveGroupedItemFromList={
                            props.onRemoveGroupedItemFromList
                                ? async () => {
                                      await props.onRemoveGroupedItemFromList?.(item);
                                  }
                                : undefined
                        }
                    />
                );
            })}
        </div>
    );
};

(GroupedByList as any).defaultProps = {
    groups: [],
    grouping: {},
    itemComponent: GroupedByBundleItem
};

export default GroupedByList;
